import { z } from 'zod';
import { NormalizedInboundMessageSchema } from './normalized-inbound-message.js';
import { NormalizedOutboundMessageSchema } from './normalized-outbound-message.js';
import { SendReceiptSchema } from './send-receipt.js';
/**
 * Generic event envelope shared by every topic on the bus. All payloads ride
 * under the same shape so consumers can branch on `topic` and Zod-validate the
 * `payload` only when needed.
 *
 * `idempotencyKey` is `${channel_kind}:${channel_account_id}:${external_message_id}`
 * for inbound ingestion events (the message uniqueness key is per-account since
 * `external_message_id` is only unique WITHIN a mailbox) and adapter-specific
 * otherwise; here we only require it's present.
 */
export const makeEventEnvelopeSchema = (topic, payload) => z.object({
    topic: z.literal(topic),
    idempotencyKey: z.string().min(1),
    emittedAt: z.string().datetime({ offset: true }),
    payload,
});
/** Topic constants, matching §7 of the design doc. */
export const EVENT_TOPICS = {
    INBOUND_NORMALIZED: 'channel.inbound.normalized',
    OUTBOUND_SEND: 'channel.outbound.send',
    OUTBOUND_DELIVERY_STATUS: 'channel.outbound.delivery_status',
    CONVERSATION_EVENT: 'conversation.event',
    CONVERSATION_CLASSIFIED: 'conversation.classified',
    CONVERSATION_IDENTIFIED: 'conversation.identified',
    CONVERSATION_SUBSCRIPTION_ENRICHED: 'conversation.subscription_enriched',
    CONVERSATION_PIPELINE_PROGRESSED: 'conversation.pipeline_progressed',
    CONVERSATION_PIPELINE_RUN_REQUESTED: 'conversation.pipeline_run_requested',
    CONVERSATION_STATUS_CHANGED: 'conversation.status_changed',
    CONVERSATION_MERGED: 'conversation.merged',
    TELEGRAM_RERENDER_ALL: 'telegram.rerender_all',
    SCHEDULED_EMAIL_DUE: 'conversation.scheduled_email_due',
    CONVERSATION_AUTO_TRANSLATED: 'conversation.auto_translated',
    CONVERSATION_RECLASSIFY_REQUESTED: 'conversation.reclassify_requested',
    CONVERSATION_SENDER_CAPACITY_OVERRIDDEN: 'conversation.sender_capacity_overridden',
    CONVERSATION_PIPELINE_WAIT_TIMED_OUT: 'conversation.pipeline_wait_timed_out',
    CONVERSATION_PIPELINE_OPERATOR_TOOK_OVER: 'conversation.pipeline_operator_took_over',
    CHANNEL_HEALTH_CHANGED: 'channel.health_changed',
    QUEUE_BACKLOG: 'bus.queue_backlog',
    // `risk.` — the payment-risk domain's own prefix. Deliberately neither
    // `conversation.` nor `channel.`: the event belongs to neither a dialogue nor
    // a communication channel. Further sources (Stripe) land under the same one.
    RISK_ALERT_INGESTED: 'risk.alert.ingested',
    RISK_CASE_IDENTIFIED: 'risk.case.identified',
    RISK_CASE_ESCALATED: 'risk.case.escalated',
    RISK_CASE_REACTION_REQUESTED: 'risk.case.reaction_requested',
};
/**
 * Logical consumer groups for bus fan-out. Each real subscriber declares one;
 * the BullMQ adapter delivers every event to every registered group for a
 * topic (see `@wit-support/app-runtime` consumer-group registry). Distinct
 * groups each receive the event; same-group subscribers compete (scaling).
 */
export const CONSUMER_GROUPS = {
    TELEGRAM: 'telegram',
    PUSH: 'push',
    IDENTITY: 'identity',
    CLASSIFIER: 'classifier',
    PIPELINE: 'pipeline',
    SUBSCRIPTION: 'subscription',
    SCHEDULED_SENDER: 'scheduled-sender',
    AUTO_TRANSLATE: 'auto-translate',
    RECLASSIFIER: 'reclassifier',
    PIPELINE_RESUME: 'pipeline-resume',
    CAPACITY_OVERRIDE: 'capacity-override',
    RISK_IDENTIFIER: 'risk-identifier',
    RISK_REACTOR: 'risk-reactor',
};
/**
 * Payload of the `INBOUND_NORMALIZED` event written to `event_outbox`.
 *
 * This is deliberately a SUPERSET of `NormalizedInboundMessage` — the shape an
 * adapter produces via `ctx.emit` — augmented with two internal DB ids minted
 * inside `MessageStorePg.insertInbound`:
 *   - `conversationId`: the DB `conversation.id` (a UUID) the Threading v2
 *     resolver returned.
 *   - `messageRowId`: the DB `message.id` (a UUID) of the persisted row. This
 *     is DISTINCT from `messageId` on the base shape — that one is the
 *     adapter's synthetic UUID, which is NOT a DB row id. Consumers that need
 *     to load/key the stored message (e.g. durable translation lookups) MUST
 *     use `messageRowId`.
 *
 * Why a separate schema instead of adding the fields to
 * `NormalizedInboundMessageSchema`: adapters emit the normalized message
 * BEFORE any conversation/message row exists, so neither id can be required
 * there without breaking every adapter and the contract-test harness. The
 * event, however, is only ever built after the row is inserted, so we require
 * both here. Consumers (e.g. the push notifier deep-link) can rely on these
 * being real DB ids rather than channel-native keys.
 */
export const InboundNormalizedPayloadSchema = NormalizedInboundMessageSchema.extend({
    conversationId: z.guid(),
    messageRowId: z.guid(),
});
export const InboundNormalizedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.INBOUND_NORMALIZED, InboundNormalizedPayloadSchema);
export const OutboundSendEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.OUTBOUND_SEND, NormalizedOutboundMessageSchema);
export const OutboundDeliveryStatusEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.OUTBOUND_DELIVERY_STATUS, z.object({
    messageId: z.guid(),
    receipt: SendReceiptSchema,
}));
export const ConversationEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_EVENT, z.object({
    conversationId: z.guid(),
    kind: z.string().min(1),
    payload: z.record(z.string(), z.unknown()),
}));
/**
 * Auto-pipeline block on the classified envelope. `pipelines` is the deduped
 * union of every auto-eligible topic's pipeline keys (Task 4 — plural).
 *
 * Back-compat: events persisted before Task 4 carried a singular `topicKey`
 * instead of `pipelines`. Both fields are optional here so readers can tolerate
 * either shape — new writers always populate `pipelines`; `topicKey` is kept as
 * an optional legacy mirror so old stored envelopes still parse.
 */
export const ClassifiedAutoPipelineSchema = z.object({
    eligible: z.boolean(),
    pipelines: z.array(z.string()).default([]),
    topicKey: z.string().nullable().optional(),
});
export const ConversationClassifiedPayloadSchema = z.object({
    conversationId: z.guid(),
    primary: z.object({ key: z.string(), name: z.string() }),
    topics: z.array(z.object({ key: z.string(), name: z.string() })),
    autoPipeline: ClassifiedAutoPipelineSchema,
    /**
     * D2. The `message.id` whose classification produced this envelope — the unit
     * of work once `pipeline.rerun_on_reply` is on (a conversation is classified
     * per MESSAGE, not once). The pipeline runner folds it into the run's
     * idempotency key so a customer's follow-up gets its own run.
     *
     * OPTIONAL, and that is load-bearing (same reason as `requestedBy` on
     * `ConversationPipelineRunRequestedPayload`): envelopes already sitting in
     * `event_outbox` at deploy time carry no `messageRowId`, and a required field
     * would make them fail to parse — the relay would then reject every one of
     * them. Readers MUST fall back to the per-conversation key when it is absent.
     */
    messageRowId: z.guid().optional(),
});
export const ConversationClassifiedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_CLASSIFIED, ConversationClassifiedPayloadSchema);
/**
 * Pure card-update trigger emitted on the first successful identity resolution
 * for a conversation. Carries only the conversation id — the Telegram bot
 * re-renders the card from canonical DB state (`contact.wit_user_id` + the
 * latest `found` resolution row), so no identity attributes ride on the wire.
 */
export const ConversationIdentifiedPayloadSchema = z.object({
    conversationId: z.guid(),
});
export const ConversationIdentifiedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_IDENTIFIED, ConversationIdentifiedPayloadSchema);
/**
 * Pure card-update trigger emitted after a contact's subscription snapshot is
 * (re)written by the `subscription-enricher` worker handler. Carries only the
 * conversation id — the Telegram bot re-renders the card from the persisted
 * `contact_subscription` snapshot, so no billing detail rides on the wire.
 */
export const ConversationSubscriptionEnrichedPayloadSchema = z.object({
    conversationId: z.guid(),
});
export const ConversationSubscriptionEnrichedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_SUBSCRIPTION_ENRICHED, ConversationSubscriptionEnrichedPayloadSchema);
/**
 * Pure card-update trigger emitted after a pipeline run advances (created /
 * stepped / finished) for a conversation. Carries only the conversation id —
 * the Telegram bot re-renders the card from canonical DB state (`pipeline_run`
 * joined to `pipeline`), so no run detail rides on the wire.
 */
export const ConversationPipelineProgressedPayloadSchema = z.object({
    conversationId: z.guid(),
});
export const ConversationPipelineProgressedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_PIPELINE_PROGRESSED, ConversationPipelineProgressedPayloadSchema);
/**
 * Operator-initiated trigger to run a SPECIFIC pipeline against a conversation,
 * including a DISABLED one ("disabled" = no AUTO run, but a manual run is
 * allowed). Emitted by admin-api after it has validated the pipeline is
 * rule-eligible (its trigger matches the conversation's labels); the worker's
 * `pipeline-run-requested` handler loads the pipeline by key (enabled OR not)
 * and runs it. `requestedBy` carries the acting admin id for audit/trace; it's
 * optional so a system-initiated request can omit it.
 */
export const ConversationPipelineRunRequestedPayloadSchema = z.object({
    conversationId: z.guid(),
    pipelineKey: z.string().min(1),
    /**
     * The pipeline_run id the admin-api atomically RESERVED for this manual run
     * (Codex #70 P1). The worker executes the graph against THIS run rather than
     * minting its own — so a concurrent double-trigger is rejected at the HTTP
     * boundary (it loses the `pipeline_run_one_active` race) instead of producing a
     * second run + a second customer email.
     */
    runId: z.guid(),
    requestedBy: z.string().nullable().optional(),
});
export const ConversationPipelineRunRequestedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_PIPELINE_RUN_REQUESTED, ConversationPipelineRunRequestedPayloadSchema);
/**
 * The six cross-cutting conversation lifecycle statuses. Mirrors the
 * `ConversationStatus` enum in `@wit-support/db` (the canonical source of
 * truth); duplicated here so the contract has no DB dependency.
 */
export const ConversationStatusValueSchema = z.enum([
    'open',
    'pending',
    'waiting',
    'resolved',
    'snoozed',
    'closed',
]);
/**
 * Pure card-update trigger emitted by `updateConversationStatus` (the single
 * status-write choke point used by the admin route AND the pipeline action's
 * port) whenever a conversation's lifecycle `status` ACTUALLY changes. A no-op
 * same-status write emits nothing. The Telegram bot consumes this to re-render
 * the one affected card from canonical DB state so admin/pipeline status
 * changes never diverge from the card. `previousStatus` is `null` only in the
 * (currently unreachable) case of a first-ever status with no prior value.
 */
export const ConversationStatusChangedPayloadSchema = z.object({
    conversationId: z.guid(),
    status: ConversationStatusValueSchema,
    previousStatus: ConversationStatusValueSchema.nullable(),
});
export const ConversationStatusChangedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_STATUS_CHANGED, ConversationStatusChangedPayloadSchema);
/**
 * A conversation was merged INTO another one and is now a tombstone. Emitted
 * once PER SOURCE inside the merge transaction, which is why `conversationId`
 * is the SOURCE, not the target: the Telegram card registry is keyed on the
 * conversation, so one event per source is what lets the bot find and re-render
 * each losing card («↪ Слито в №<targetNumber>», keyboard removed). Live buttons
 * on a tombstone's card are inadmissible — a reply from Telegram would land in a
 * closed, merged-away dialogue.
 *
 * `targetNumber` is carried in the payload so the card text can be rendered
 * without a second lookup; it is nullable only for the rollout window in which a
 * conversation might predate `conversation.number`.
 */
export const ConversationMergedPayloadSchema = z.object({
    /** The SOURCE conversation — the one that just became a tombstone. */
    conversationId: z.guid(),
    targetId: z.guid(),
    targetNumber: z.number().int().nullable(),
});
export const ConversationMergedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_MERGED, ConversationMergedPayloadSchema);
/**
 * Emitted by the auto-translator worker handler after a NEW Russian translation
 * row is stored for an inbound message. Carries both ids so the Telegram bot can
 * locate and re-render the conversation card (the bot refetches the stored
 * translation). Only fires when `llm.auto_translate_inbound.enabled` is true AND the
 * translation was newly written (not a cache hit, not already-target skip).
 */
export const AutoTranslatedPayloadSchema = z.object({
    conversationId: z.guid(),
    messageRowId: z.guid(),
});
export const ConversationAutoTranslatedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_AUTO_TRANSLATED, AutoTranslatedPayloadSchema);
/**
 * Operator-initiated request to RE-RUN classification on a conversation. Emitted
 * by admin-api; consumed by the worker's `reclassify` handler (group RECLASSIFIER).
 * The handler re-classifies over the chosen `scope` and overwrites the stored
 * topics + `topic_classified` event WITHOUT emitting `conversation.classified`,
 * so a manual reclassify triggers NO pipelines and NO Telegram re-fan.
 * `scope`: `first_message` = first inbound message only; `full_thread` = the whole
 * conversation transcript (both directions), chronological.
 */
export const ReclassifyScopeSchema = z.enum(['first_message', 'full_thread']);
/**
 * In what capacity the sender writes — a private person vs on behalf of an
 * organization. Canonical source for the `senderCapacity` field surfaced on
 * `ConversationDTO`/`ConversationListItemDTO` (`@wit-support/admin-contracts`)
 * and the classifier's `SenderCapacity` domain type
 * (`@wit-support/classification`, kept dependency-free of `contracts`).
 */
export const SenderCapacitySchema = z.enum(['customer', 'business']);
export const ConversationReclassifyRequestedPayloadSchema = z.object({
    conversationId: z.guid(),
    scope: ReclassifyScopeSchema,
    requestedBy: z.string().nullable(),
    /**
     * Publish `conversation.classified` after the re-classification lands, so the
     * downstream pipeline + Telegram consumers DO fire.
     *
     * Default `false` keeps the operator-initiated semantics above intact. It is
     * set to `true` by exactly one producer: the processing-gap sweeper repairing a
     * FIRST classification that was lost (LLM outage, exhausted retries, Redis
     * restart without AOF). In that case the pipelines never ran at all, so they
     * must run now — otherwise the repair rewrites the labels and still leaves the
     * customer without an answer.
     *
     * OPTIONAL by contract, never required: envelopes already sitting in
     * `event_outbox` at deploy time were written without the field and must keep
     * parsing. Same idiom as `requestedBy` in
     * `ConversationPipelineRunRequestedPayload` (see the `parsedBare` test in
     * `packages/contracts/src/index.test.ts`).
     */
    emitClassified: z.boolean().optional(),
});
export const ConversationReclassifyRequestedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_RECLASSIFY_REQUESTED, ConversationReclassifyRequestedPayloadSchema);
/**
 * Emitted when an operator (supervisor+) manually overrides — or clears — the
 * classifier's `sender_capacity` verdict for a conversation. Consumer: the
 * worker's `capacity-override` handler (`CONSUMER_GROUPS.CAPACITY_OVERRIDE`).
 * It recomputes the gate decision against the STORED labels using the new
 * effective capacity and, if that decision flips from "not eligible" to
 * "eligible", emits `conversation.classified` so the ordinary pipeline runner
 * starts the pipelines that now apply. `value = null` means the override was
 * cleared (the model's own verdict is effective again). `actorId` is the
 * acting admin id; nullable for a system-initiated clear.
 */
export const ConversationSenderCapacityOverriddenPayloadSchema = z.object({
    conversationId: z.guid(),
    value: SenderCapacitySchema.nullable(),
    actorId: z.string().nullable(),
});
export const ConversationSenderCapacityOverriddenEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_SENDER_CAPACITY_OVERRIDDEN, ConversationSenderCapacityOverriddenPayloadSchema);
/**
 * Single bulk trigger to re-render EVERY already-posted Telegram card to its
 * current DB state (edit-in-place, never re-send). Emitted by an admin action;
 * the bot fans out internally over `telegram_notification.conversation_id` and
 * calls the idempotent `renderCardFromState` per card. The payload carries only
 * the acting admin id for audit/trace; it's optional so a system-initiated
 * request can omit it.
 */
export const TelegramRerenderAllPayloadSchema = z.object({
    requestedBy: z.string().nullable().optional(),
});
export const TelegramRerenderAllEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.TELEGRAM_RERENDER_ALL, TelegramRerenderAllPayloadSchema);
/**
 * Emitted by the scheduled-email sweeper when a `scheduled_email` row whose
 * `scheduled_for` has elapsed is claimed (transitioned `pending → enqueued`).
 * The worker's `scheduled-sender` handler consumes this to actually send the
 * email. Single-enqueue is guaranteed by the atomic pending→enqueued status
 * claim inside the sweeper transaction (one outbox row per due row). The
 * outbox payload is a full envelope whose `idempotencyKey` (`sched-due:${id}`)
 * drives per-group BullMQ jobId dedup on an OutboxRelay republish.
 */
export const ScheduledEmailDuePayloadSchema = z.object({
    conversationId: z.guid(),
    scheduledEmailId: z.guid(),
});
export const ScheduledEmailDueEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.SCHEDULED_EMAIL_DUE, ScheduledEmailDuePayloadSchema);
/**
 * Emitted by the pipeline resume scheduler when a `wait` step's hold period
 * has elapsed for a pipeline run. The `pipeline-resume` consumer picks this up
 * and advances the run past the `wait` node. `conversationId` + `runId` are
 * the minimal locator pair needed to reload and resume the correct run.
 */
export const PipelineWaitTimedOutPayloadSchema = z.object({
    conversationId: z.guid(),
    runId: z.guid(),
});
export const PipelineWaitTimedOutEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_PIPELINE_WAIT_TIMED_OUT, PipelineWaitTimedOutPayloadSchema);
/**
 * Emitted by `ReplySendPg.reply()` when an OPERATOR sends a manual reply into a
 * conversation that has a parked (`waiting`) pipeline run. The human took the
 * exchange over, so the bot must stop: the `pipeline-resume` consumer un-parks
 * the run down its `escalated` branch and the customer's next message no longer
 * resumes anything.
 *
 * Deliberately stated over the RUN STATUS, not over any pipeline or node: any
 * node that suspends a run (today only `collect_input`) leaves it `waiting` with
 * an `awaiting` snapshot, and a human answering by hand outranks all of them
 * alike. Nothing here inspects a pipeline key or name.
 *
 * OPERATOR, not automation: the emit is gated on `actor !== SYSTEM_ACTOR_ID`,
 * the only actor a pipeline send can carry (`apps/worker/src/adapters/
 * reply-port.ts`). A scheduled email an operator queued themselves DOES carry
 * their admin id and therefore counts as taking over — deliberately: it is a
 * manual answer, only deferred.
 *
 * `runId` is the parked run observed at send time; the handler re-checks it is
 * still waiting, so a stale event (the run already resumed / timed out) is a
 * no-op. `actorAdminId` is audit only.
 *
 * `conversationId` is the RUN's own dialog, which need not be the one the
 * operator typed in: the emitter releases every park of the CONTACT, because the
 * resume path reaches that far too (a reply filed in a sibling dialog resumes a
 * park anywhere in the contact's dialogs). `replyConversationId` names where the
 * human actually answered and is present ONLY when the two differ — absent on
 * every ordinary takeover, and absent on every envelope written before the
 * contact-wide release shipped, which is why it is optional.
 */
export const PipelineOperatorTookOverPayloadSchema = z.object({
    conversationId: z.guid(),
    runId: z.guid(),
    actorAdminId: z.guid().nullable(),
    replyConversationId: z.guid().optional(),
});
export const PipelineOperatorTookOverEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CONVERSATION_PIPELINE_OPERATOR_TOOK_OVER, PipelineOperatorTookOverPayloadSchema);
/**
 * Emitted by the channel-health sweeper when a channel's OBSERVED health
 * changes (or, for a reminder, when it has been unhealthy past the reminder
 * interval). Consumed by the Telegram bot, which posts a plain-text
 * infrastructure alert to the unfiltered destinations.
 *
 * There is no `conversationId` — this is the first event on the bus that is
 * about the plumbing rather than about a dialogue. `channel_account.status`
 * intentionally does NOT change (see the `channel_health` migration), so the
 * only way an operator learns about a dead channel is this event plus the
 * health pill in the admin channel list.
 */
export const CHANNEL_HEALTH_VALUES = ['ok', 'auth_failed', 'failing', 'stalled', 'silent'];
export const ChannelHealthSchema = z.enum(CHANNEL_HEALTH_VALUES);
/** Which detector produced the verdict — surfaced in the alert text. */
export const ChannelHealthReasonSchema = z.enum(['poll', 'send', 'stall', 'silence', 'recovered']);
export const ChannelHealthChangedPayloadSchema = z.object({
    channelAccountId: z.guid(),
    kind: z.string(),
    name: z.string(),
    outboundIdentity: z.string().nullable(),
    health: ChannelHealthSchema,
    previousHealth: ChannelHealthSchema,
    reason: ChannelHealthReasonSchema,
    /** Full provider text, `error_description` included. */
    error: z.string().nullable(),
    lastSuccessAt: z.string().nullable(),
    detectedAt: z.string(),
    /** True when this is a periodic reminder about an ongoing outage, not a transition. */
    reminder: z.boolean(),
});
export const ChannelHealthChangedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.CHANNEL_HEALTH_CHANGED, ChannelHealthChangedPayloadSchema);
/**
 * A BullMQ fan-out queue has jobs stuck in its `failed` set — events that burned
 * every retry attempt and are gone (S11).
 *
 * Why a NEW topic and not `channel.health_changed`: that payload is
 * channel-shaped and REQUIRES `channelAccountId` (a uuid), `kind` and
 * `outboundIdentity`. A dead queue has none of those — it is infrastructure-wide,
 * not attributable to any channel — so reusing it would mean inventing a fake
 * channel identity for every alert. Every other existing topic is conversation-
 * scoped. Nothing fits, so this is the one genuinely new event in D15.
 */
export const QueueBacklogPayloadSchema = z.object({
    /** Full BullMQ queue name, `${topic}#${group}`. */
    queue: z.string(),
    topic: z.string(),
    group: z.string(),
    /** Size of the `failed` set at detection time. 0 on a recovery alert. */
    failed: z.number().int().nonnegative(),
    threshold: z.number().int().positive(),
    detectedAt: z.string(),
    /** True when this repeats an ongoing backlog rather than announcing a new one. */
    reminder: z.boolean(),
});
export const QueueBacklogEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.QUEUE_BACKLOG, QueueBacklogPayloadSchema);
/**
 * A payment-risk alert was loaded from a source and attached to a case.
 *
 * Written to `event_outbox` IN THE SAME TRANSACTION as the alert upsert
 * (`RiskAlertStorePg.upsertAlert`), and only when the alert is NEW or its
 * `content_hash` changed — otherwise every 5-minute poll tick would reprocess
 * the whole history. One consumer: the worker's `risk-alert-identifier`
 * handler (group RISK_IDENTIFIER), which identifies the customer behind the
 * case.
 *
 * The payload deliberately carries NO alert fields. The handler re-reads fresh
 * state from the DB: between writing the event and handling it the poller may
 * have updated the alert, and correlation may have re-attached it to a
 * different case. The event is a "look at this row" signal, not a snapshot.
 *
 * `source` is a literal rather than a free string while exactly one source
 * exists: publishing an event from a source that does not exist must fail at
 * the boundary rather than reach the handler. The Stripe wave widens it to an
 * enum — one line, and an explicit decision rather than a silent widening.
 */
export const RiskAlertIngestedPayloadSchema = z.object({
    alertId: z.guid(),
    caseId: z.guid(),
    source: z.literal('chargeblast'),
    sourceAlertId: z.string().min(1),
});
export const RiskAlertIngestedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.RISK_ALERT_INGESTED, RiskAlertIngestedPayloadSchema);
/**
 * A case's identification outcome (customer match against the case's
 * contact evidence) is now known — written by the `risk-alert-identifier`
 * handler in the same transaction as the identification write. Consumed by
 * `RISK_REACTOR`, which decides whether/which reaction rule fires for the
 * case now that its identification status is settled.
 */
export const RiskCaseIdentifiedPayloadSchema = z.object({
    caseId: z.guid(),
    identificationStatus: z.enum(['resolved', 'ambiguous', 'not_found', 'unavailable']),
});
export const RiskCaseIdentifiedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.RISK_CASE_IDENTIFIED, RiskCaseIdentifiedPayloadSchema);
/**
 * A case crossed a reaction rule's escalation threshold (see Task 10 for the
 * rule engine). `reason` is a machine-readable code, not prose — the one
 * consumer, `apps/telegram-bot` (group TELEGRAM), reads the case fresh from
 * the DB itself and renders the alert from stored state rather than from this
 * payload, so the payload stays minimal by canon (cf. `RiskAlertIngestedPayloadSchema`).
 */
export const RiskCaseEscalatedPayloadSchema = z.object({
    caseId: z.guid(),
    reason: z.string().min(1),
});
export const RiskCaseEscalatedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.RISK_CASE_ESCALATED, RiskCaseEscalatedPayloadSchema);
/**
 * An admin manually asked for a case's reaction to be (re-)evaluated. Routed
 * to `RISK_REACTOR`, the same group that reacts to `RISK_CASE_IDENTIFIED`.
 */
export const RiskCaseReactionRequestedPayloadSchema = z.object({
    caseId: z.guid(),
    requestedByAdminId: z.guid(),
});
export const RiskCaseReactionRequestedEventSchema = makeEventEnvelopeSchema(EVENT_TOPICS.RISK_CASE_REACTION_REQUESTED, RiskCaseReactionRequestedPayloadSchema);
/** Discriminated union of every event shape on the bus. */
export const ChannelEventSchema = z.discriminatedUnion('topic', [
    InboundNormalizedEventSchema,
    OutboundSendEventSchema,
    OutboundDeliveryStatusEventSchema,
    ConversationEventSchema,
    ConversationClassifiedEventSchema,
    ConversationIdentifiedEventSchema,
    ConversationSubscriptionEnrichedEventSchema,
    ConversationPipelineProgressedEventSchema,
    ConversationPipelineRunRequestedEventSchema,
    ConversationStatusChangedEventSchema,
    ConversationMergedEventSchema,
    TelegramRerenderAllEventSchema,
    ScheduledEmailDueEventSchema,
    ConversationAutoTranslatedEventSchema,
    ConversationReclassifyRequestedEventSchema,
    ConversationSenderCapacityOverriddenEventSchema,
    PipelineWaitTimedOutEventSchema,
    PipelineOperatorTookOverEventSchema,
    ChannelHealthChangedEventSchema,
    QueueBacklogEventSchema,
    RiskAlertIngestedEventSchema,
    RiskCaseIdentifiedEventSchema,
    RiskCaseEscalatedEventSchema,
    RiskCaseReactionRequestedEventSchema,
]);
//# sourceMappingURL=events.js.map